import React from 'react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../components/ContactForm';

// Curated Unsplash images (natural resources / Argentina themes)
const IMG_DASH = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=70';
const IMG_HERO = 'https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=1600&q=70';
const IMG_MAP  = 'https://images.unsplash.com/photo-1509316785289-025f5b846b35?auto=format&fit=crop&w=1400&q=70';

/* Unique on-brand SVG illustrations for each sector card */
function SectorArt({ variant }) {
  if (variant === 'lithium') {
    return (
      <svg viewBox="0 0 400 220" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id="li-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e8dbc9"/>
            <stop offset="1" stopColor="#f5f5f2"/>
          </linearGradient>
          <linearGradient id="li-ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#c9c2b0"/>
            <stop offset="1" stopColor="#e6e0d0"/>
          </linearGradient>
        </defs>
        <rect width="400" height="220" fill="url(#li-sky)"/>
        <path d="M0 140 L60 110 L120 128 L180 96 L240 118 L320 88 L400 108 L400 220 L0 220 Z" fill="#a09076" opacity="0.55"/>
        <rect x="0" y="150" width="400" height="70" fill="url(#li-ground)"/>
        {[...Array(12)].map((_, i) => (
          <g key={i} transform={`translate(${20 + i * 32}, ${168 + (i % 2) * 8}) scale(0.9)`}>
            <polygon points="10,0 20,6 20,18 10,24 0,18 0,6" fill="none" stroke="#0b2545" strokeWidth="1" opacity="0.5"/>
          </g>
        ))}
        <g transform="translate(280, 40)">
          <circle cx="30" cy="30" r="28" fill="#0b2545"/>
          <text x="30" y="38" textAnchor="middle" fontFamily="'IBM Plex Serif', serif" fontSize="22" fontWeight="600" fill="#f5f5f2">Li</text>
        </g>
      </svg>
    );
  }
  if (variant === 'oilgas') {
    return (
      <svg viewBox="0 0 400 220" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <linearGradient id="og-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0b2545"/>
            <stop offset="1" stopColor="#1a3a6b"/>
          </linearGradient>
        </defs>
        <rect width="400" height="220" fill="url(#og-sky)"/>
        {[0,1,2,3,4,5].map((i) => (
          <line key={i} x1="0" y1={150 + i*12} x2="400" y2={150 + i*12} stroke="#c66b3d" strokeWidth="0.6" opacity={0.25 + i*0.1}/>
        ))}
        {/* Pump jack */}
        <g transform="translate(70, 90)" stroke="#f5f5f2" strokeWidth="2" fill="none">
          <rect x="0" y="60" width="140" height="5" fill="#f5f5f2"/>
          <polygon points="30,60 70,10 110,60" />
          <line x1="70" y1="10" x2="20" y2="20" stroke="#c66b3d" strokeWidth="3"/>
          <polygon points="10,14 26,10 22,26" fill="#c66b3d" stroke="none"/>
          <line x1="14" y1="24" x2="14" y2="60" />
        </g>
        <g transform="translate(280, 30)">
          <circle cx="30" cy="30" r="28" fill="#f5f5f2"/>
          <text x="30" y="36" textAnchor="middle" fontFamily="'IBM Plex Serif', serif" fontSize="14" fontWeight="600" fill="#0b2545">O&amp;G</text>
        </g>
      </svg>
    );
  }
  // mining
  return (
    <svg viewBox="0 0 400 220" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="mi-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#c66b3d"/>
          <stop offset="1" stopColor="#7a3a1f"/>
        </linearGradient>
      </defs>
      <rect width="400" height="220" fill="url(#mi-sky)"/>
      {[0,1,2,3,4].map((i) => (
        <path
          key={i}
          d={`M0 ${90 + i*24} L${60 + i*28} ${90 + i*24} L${100 + i*28} ${110 + i*24} L400 ${110 + i*24} L400 220 L0 220 Z`}
          fill="#0b2545"
          opacity={0.15 + i*0.14}
        />
      ))}
      <g transform="translate(60, 50)">
        <circle cx="30" cy="30" r="28" fill="#f5f5f2"/>
        <text x="30" y="36" textAnchor="middle" fontFamily="'IBM Plex Serif', serif" fontSize="16" fontWeight="600" fill="#0b2545">Cu</text>
      </g>
      <g transform="translate(310, 40)">
        <circle cx="24" cy="24" r="22" fill="#f5f5f2" opacity="0.9"/>
        <text x="24" y="30" textAnchor="middle" fontFamily="'IBM Plex Serif', serif" fontSize="14" fontWeight="600" fill="#0b2545">Au</text>
      </g>
    </svg>
  );
}

function MiniChart() {
  const points = [[0,60],[10,55],[20,58],[30,46],[40,50],[50,40],[60,44],[70,30],[80,34],[90,22],[100,26],[110,18],[120,22],[130,12]];
  const path = points.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(' ');
  const areaPath = `${path} L 130,80 L 0,80 Z`;
  return (
    <svg viewBox="0 0 140 80" width="100%" height="100%" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0b2545" stopOpacity="0.35"/>
          <stop offset="1" stopColor="#0b2545" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#cg)"/>
      <path d={path} fill="none" stroke="#0b2545" strokeWidth="1.4"/>
    </svg>
  );
}

function NumberedLabel({ n }) {
  return (
    <div style={{
      width: 40, height: 40, border: '1px solid var(--gd-navy)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'IBM Plex Serif, Georgia, serif', fontSize: 18, color: 'var(--gd-navy)', fontWeight: 600,
    }}>{n}</div>
  );
}

const scrollToId = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function HomePage() {
  const { t } = useTranslation();

  return (
    <>
      {/* HERO */}
      <section id="overview" style={{ paddingTop: 72, paddingBottom: 72, background: 'linear-gradient(180deg, #fafaf7 0%, #ffffff 100%)' }}>
        <div className="container-x">
          <div className="grid gap-10 lg:grid-cols-12 items-center">
            <div className="lg:col-span-7">
              <span className="eyebrow">{t('hero.kicker')}</span>
              <h1 className="font-serif mt-5" style={{ fontSize: 'clamp(34px, 5vw, 56px)', lineHeight: 1.05, color: 'var(--gd-navy)', letterSpacing: '-0.02em' }}>
                {t('hero.title')}
              </h1>
              <p className="mt-6" style={{ fontSize: 17, color: '#334155', lineHeight: 1.65, maxWidth: 680 }}>
                {t('hero.lead')}
              </p>
              <p className="mt-4" style={{ fontSize: 17, color: '#334155', lineHeight: 1.65, maxWidth: 680 }}>
                {t('hero.lead2')}
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <a href="#contact" onClick={scrollToId('contact')} className="btn-primary">
                  {t('hero.ctaPrimary')}
                </a>
                <a href="#why" onClick={scrollToId('why')} className="btn-outline">
                  {t('hero.ctaSecondary')}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div style={{ position: 'relative' }}>
                <div style={{ border: '1px solid var(--gd-line)', background: 'white' }}>
                  <img src={IMG_HERO} alt="Paisaje del Triángulo del Litio, Argentina" style={{ display: 'block', width: '100%', height: 380, objectFit: 'cover' }} loading="lazy"/>
                  <div style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 16, borderTop: '1px solid var(--gd-line)' }}>
                    <div style={{ width: 90, height: 44 }}><MiniChart /></div>
                    <div>
                      <div className="stat-num" style={{ fontSize: 22 }}>{t('hero.stat1.value')}</div>
                      <div className="stat-lbl">{t('hero.stat1.label')}</div>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block" style={{ position: 'absolute', right: -16, bottom: -20, background: 'var(--gd-navy)', color: 'white', padding: '14px 18px', maxWidth: 240, boxShadow: '0 20px 40px rgba(11,37,69,0.18)' }}>
                  <div className="stat-num" style={{ color: 'white', fontSize: 24 }}>{t('hero.stat2.value')}</div>
                  <div style={{ fontSize: 12, color: '#b7c4d8', marginTop: 4, lineHeight: 1.4 }}>{t('hero.stat2.label')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-3" style={{ borderTop: '1px solid var(--gd-line)', paddingTop: 32 }}>
            <div>
              <div className="stat-num">{t('hero.stat1.value')}</div>
              <div className="stat-lbl">{t('hero.stat1.label')}</div>
            </div>
            <div>
              <div className="stat-num">{t('hero.stat2.value')}</div>
              <div className="stat-lbl">{t('hero.stat2.label')}</div>
            </div>
            <div>
              <div className="stat-num">{t('hero.stat3.value')}</div>
              <div className="stat-lbl">{t('hero.stat3.label')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section id="sectors" style={{ background: 'var(--gd-bone)', padding: '96px 0' }}>
        <div className="container-x">
          <div className="mb-12">
            <span className="eyebrow">{t('sectors.subtitle')}</span>
            <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--gd-navy)', letterSpacing: '-0.01em' }}>
              {t('sectors.title')}
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              { key: 'lithium', variant: 'lithium', chip: 'Li' },
              { key: 'oilgas',  variant: 'oilgas',  chip: 'O&G' },
              { key: 'mining',  variant: 'mining',  chip: 'Cu / Au' },
            ].map((s) => (
              <article key={s.key} className="data-card" style={{ padding: 0 }}>
                <div style={{ width: '100%', height: 200, overflow: 'hidden' }}>
                  <SectorArt variant={s.variant} />
                </div>
                <div style={{ padding: 24 }}>
                  <span className="chip">{s.chip}</span>
                  <h3 className="font-serif mt-3" style={{ fontSize: 22, color: 'var(--gd-navy)', fontWeight: 600 }}>{t(`sectors.${s.key}.title`)}</h3>
                  <p style={{ marginTop: 8, color: '#475569', lineHeight: 1.6, fontSize: 15 }}>{t(`sectors.${s.key}.body`)}</p>
                  <a href="#contact" onClick={scrollToId('contact')} className="mt-4 inline-block link-underline" style={{ fontSize: 13, fontWeight: 600, color: 'var(--gd-terra)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {t('buttons.exploreSector')} →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="section">
        <div className="container-x">
          <div className="grid gap-14 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow">2026</span>
              <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--gd-navy)', letterSpacing: '-0.01em' }}>
                {t('why.title')}
              </h2>
              <div style={{ marginTop: 24, position: 'relative' }}>
                <img src={IMG_MAP} alt="Mapa topográfico de recursos naturales en Argentina" style={{ width: '100%', height: 320, objectFit: 'cover', border: '1px solid var(--gd-line)' }} loading="lazy"/>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid gap-5 md:grid-cols-2">
                {['item1', 'item2', 'item3', 'item4'].map((k, i) => (
                  <div key={k} className="data-card">
                    <div className="flex items-center gap-3 mb-3">
                      <NumberedLabel n={String(i + 1).padStart(2, '0')} />
                      <h3 className="font-serif" style={{ fontSize: 18, color: 'var(--gd-navy)', fontWeight: 600 }}>{t(`why.${k}.title`)}</h3>
                    </div>
                    <p style={{ color: '#475569', lineHeight: 1.6, fontSize: 14.5 }}>{t(`why.${k}.body`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" style={{ background: 'var(--gd-navy)', color: 'white', padding: '96px 0' }}>
        <div className="container-x">
          <div className="grid gap-10 lg:grid-cols-12 items-end">
            <div className="lg:col-span-7">
              <span className="eyebrow" style={{ color: '#f2c9b0' }}>Workflow</span>
              <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'white', letterSpacing: '-0.01em' }}>
                {t('process.title')}
              </h2>
              <p style={{ color: '#b7c4d8', marginTop: 12, maxWidth: 620, lineHeight: 1.65 }}>{t('process.lead')}</p>
            </div>
            <div className="lg:col-span-5">
              <div style={{ border: '1px solid #1e3b6a', padding: 18, background: '#0e2a52' }}>
                <img src={IMG_DASH} alt="Panel analítico de mercado" style={{ width: '100%', height: 200, objectFit: 'cover' }} loading="lazy"/>
              </div>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mt-14">
            {['step1', 'step2', 'step3', 'step4'].map((k, i) => (
              <div key={k} style={{ borderTop: '2px solid var(--gd-terra)', paddingTop: 20 }}>
                <div style={{ fontFamily: 'IBM Plex Serif, Georgia, serif', fontSize: 42, color: 'var(--gd-terra)', lineHeight: 1, fontWeight: 600 }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="font-serif mt-3" style={{ fontSize: 19, color: 'white', fontWeight: 600 }}>{t(`process.${k}.title`)}</h3>
                <p style={{ color: '#b7c4d8', marginTop: 8, lineHeight: 1.6, fontSize: 14.5 }}>{t(`process.${k}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="section">
        <div className="container-x">
          <div className="grid gap-14 lg:grid-cols-12 items-center">
            <div className="lg:col-span-6">
              <div style={{ position: 'relative' }}>
                <div className="grid-lines-bg" style={{ position: 'absolute', inset: -12, zIndex: 0 }} />
                <div style={{ position: 'relative', zIndex: 1, background: 'white', border: '1px solid var(--gd-line)', padding: 20 }}>
                  <img src={IMG_DASH} alt="Dashboard de datos macroeconómicos" style={{ width: '100%', height: 340, objectFit: 'cover' }} loading="lazy"/>
                  <div className="grid gap-3 md:grid-cols-3 mt-4">
                    <div style={{ padding: 12, border: '1px solid var(--gd-line)' }}>
                      <div className="stat-num" style={{ fontSize: 22 }}>+38%</div>
                      <div className="stat-lbl" style={{ fontSize: 11 }}>2020–2025</div>
                    </div>
                    <div style={{ padding: 12, border: '1px solid var(--gd-line)' }}>
                      <div className="stat-num" style={{ fontSize: 22 }}>USD 78/bbl</div>
                      <div className="stat-lbl" style={{ fontSize: 11 }}>WTI ref.</div>
                    </div>
                    <div style={{ padding: 12, border: '1px solid var(--gd-line)' }}>
                      <div className="stat-num" style={{ fontSize: 22 }}>412 pozos</div>
                      <div className="stat-lbl" style={{ fontSize: 11 }}>Vaca Muerta 2025</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <span className="eyebrow">{t('transparency.kicker')}</span>
              <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--gd-navy)', letterSpacing: '-0.01em' }}>
                {t('transparency.title')}
              </h2>
              <p style={{ marginTop: 16, color: '#334155', lineHeight: 1.7 }}>{t('transparency.body1')}</p>
              <p style={{ marginTop: 12, color: '#334155', lineHeight: 1.7 }}>{t('transparency.body2')}</p>
              <ul className="mt-6 flex flex-col gap-3">
                {['bullet1', 'bullet2', 'bullet3'].map((k) => (
                  <li key={k} className="flex items-start gap-3">
                    <span style={{ marginTop: 8, width: 14, height: 1.5, background: 'var(--gd-terra)', flexShrink: 0 }} />
                    <span style={{ color: '#334155', lineHeight: 1.6, fontSize: 15 }}>{t(`transparency.${k}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" style={{ background: 'var(--gd-bone)', padding: '96px 0' }}>
        <div className="container-x">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="eyebrow">{t('contact.kicker')}</span>
              <h2 className="font-serif mt-3" style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--gd-navy)', letterSpacing: '-0.01em' }}>
                {t('contact.title')}
              </h2>
              <p style={{ marginTop: 14, color: '#475569', lineHeight: 1.65, maxWidth: 460 }}>{t('contact.lead')}</p>

              <div className="mt-8 flex flex-col gap-2" style={{ fontSize: 14, color: '#334155' }}>
                <div><strong style={{ color: 'var(--gd-navy)' }}>Av. Leandro N. Alem 855, piso 12</strong></div>
                <div>C1001AAD — CABA, Argentina</div>
                <div>+54 11 5032-8470</div>
                <div><a href="mailto:contacto@geodatosarg.com" className="link-underline" style={{ color: 'var(--gd-navy)', fontWeight: 600 }}>contacto@geodatosarg.com</a></div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div style={{ background: 'white', border: '1px solid var(--gd-line)', padding: 28 }}>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
